import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inr, fmtDate, errMsg } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function Withdrawal() {
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [upi, setUpi] = useState("");
  const [bank, setBank] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/dashboard").then((r) => setBalance(r.data.available_balance));
    api.get("/transactions?type=withdrawal").then((r) => setTxns(r.data));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/withdrawals", {
        amount: parseFloat(amount),
        upi_id: method === "upi" ? upi : "",
        bank_details: method === "bank" ? bank : "",
      });
      toast.success("Withdrawal request submitted");
      setAmount(""); setUpi(""); setBank("");
      load();
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="overline">Payout</p>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1 mb-8">Withdrawal</h1>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="bg-[#09090B] text-white p-5 rounded-sm mb-6">
            <p className="overline text-white/50">Available for Withdrawal</p>
            <p className="text-3xl font-bold font-mono-num tracking-tighter mt-1" data-testid="withdraw-available">{inr(balance)}</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="overline">Withdrawal Amount (₹)</Label>
              <Input data-testid="withdraw-amount" type="number" min="1" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-2 rounded-sm h-11 font-mono-num" placeholder="0.00" />
            </div>
            <Tabs value={method} onValueChange={setMethod}>
              <Label className="overline">Payout Method</Label>
              <TabsList className="grid grid-cols-2 w-full mt-2 rounded-sm">
                <TabsTrigger value="upi" data-testid="withdraw-method-upi" className="rounded-sm">UPI ID</TabsTrigger>
                <TabsTrigger value="bank" data-testid="withdraw-method-bank" className="rounded-sm">Bank Account</TabsTrigger>
              </TabsList>
              <TabsContent value="upi" className="mt-4">
                <Input data-testid="withdraw-upi" value={upi} onChange={(e) => setUpi(e.target.value)} className="rounded-sm h-11 font-mono-num" placeholder="name@bank" />
              </TabsContent>
              <TabsContent value="bank" className="mt-4">
                <Textarea data-testid="withdraw-bank" value={bank} onChange={(e) => setBank(e.target.value)} className="rounded-sm" rows={3} placeholder="Account no, IFSC, Account holder name" />
              </TabsContent>
            </Tabs>
            <Button data-testid="withdraw-submit" type="submit" disabled={loading} className="w-full h-11 rounded-sm active:scale-[0.98] transition-transform">
              {loading ? "Submitting…" : "Request Withdrawal"}
            </Button>
          </form>
        </div>

        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-lg mb-3">How payouts work</h2>
          <ul className="text-sm text-muted-foreground space-y-3 list-none">
            <li className="flex gap-3"><span className="font-mono-num text-foreground">01</span> Amount is held from your balance immediately on request.</li>
            <li className="flex gap-3"><span className="font-mono-num text-foreground">02</span> Admin reviews and processes the payout manually.</li>
            <li className="flex gap-3"><span className="font-mono-num text-foreground">03</span> Once paid, a UTR is attached and status becomes <StatusBadge status="paid" />.</li>
            <li className="flex gap-3"><span className="font-mono-num text-foreground">04</span> If rejected, the held amount is released back to your balance.</li>
          </ul>
        </div>
      </div>

      <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Withdrawal History</h2>
      <div className="border border-border rounded-sm overflow-x-auto bg-card">
        <table className="w-full text-sm" data-testid="withdrawal-history">
          <thead>
            <tr className="text-left overline border-b border-border">
              <th className="px-5 py-3 font-semibold">Amount</th>
              <th className="px-5 py-3 font-semibold hidden sm:table-cell">Payout To</th>
              <th className="px-5 py-3 font-semibold">UTR</th>
              <th className="px-5 py-3 font-semibold hidden md:table-cell">Date &amp; Time</th>
              <th className="px-5 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 ? (
              <tr><td colSpan="5" className="px-5 py-6 text-muted-foreground">No withdrawals yet.</td></tr>
            ) : txns.map((t) => (
              <tr key={t.id} className="border-b border-border/40 last:border-0 hover:bg-black/5">
                <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                <td className="px-5 py-3 text-xs hidden sm:table-cell">{t.upi_id || t.bank_details || "—"}</td>
                <td className="px-5 py-3 font-mono-num text-xs">{t.status === "paid" ? (t.utr || "—") : "—"}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
